/**
 * Delete Confirmation Modal Component
 * Displays record details and requires ceremonial checkbox before deletion
 */

import React, { useState, useCallback } from 'react';
import { Modal, Button, Checkbox, Message, Icon } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { PageUrlElementModel, PageUrlType } from '../types';
import { i18nKeys, URL_TYPE_DISPLAY_NAMES } from '../constants';
import styles from '../UrlUnlocking.module.css';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  item: PageUrlElementModel | null;
  isDeleting: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Get display name for URL type
 */
const getUrlTypeDisplayName = (urlType: PageUrlType): string => {
  return URL_TYPE_DISPLAY_NAMES[urlType] || `Type ${urlType}`;
};

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  item,
  isDeleting,
  error,
  onConfirm,
  onCancel
}) => {
  const [isAccepted, setIsAccepted] = useState(false);

  const handleClose = useCallback(() => {
    setIsAccepted(false);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    if (isAccepted) {
      onConfirm();
    }
  }, [isAccepted, onConfirm]);

  // Reset checkbox when modal opens with new item
  React.useEffect(() => {
    if (isOpen) {
      setIsAccepted(false);
    }
  }, [isOpen, item?.id]);

  if (!item) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="small"
      className={styles.deleteModal}
    >
      <Modal.Header>
        <Icon name="warning sign" color="red" />
        <Translate value={i18nKeys.DELETE_MODAL.TITLE} />
      </Modal.Header>
      
      <Modal.Content>
        {/* Record Details */}
        <div className={styles.deleteModalDetails}>
          <div className={styles.deleteModalRow}>
            <strong><Translate value={i18nKeys.RESULTS.ID} />:</strong>
            <span>{item.id}</span>
          </div>
          <div className={styles.deleteModalRow}>
            <strong><Translate value={i18nKeys.RESULTS.URL_TYPE} />:</strong>
            <span>{getUrlTypeDisplayName(item.urlType as PageUrlType)}</span>
          </div>
          <div className={styles.deleteModalRow}>
            <strong><Translate value={i18nKeys.RESULTS.URL_PATH} />:</strong>
            <code>{item.urlPath}</code>
          </div>
          
          {/* Linked entities */}
          {item.organization && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.ORGANIZATION} />:</strong>
              <span>{item.organization.name || `ID: ${item.organization.id}`}</span>
            </div>
          )}
          {item.event && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.EVENT} />:</strong>
              <span>{item.event.name || `ID: ${item.event.id}`}</span>
            </div>
          )}
          {item.campaign && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.CAMPAIGN} />:</strong>
              <span>{item.campaign.name || `ID: ${item.campaign.id}`}</span>
            </div>
          )}
          {item.series && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.SERIES} />:</strong>
              <span>{item.series.name || `ID: ${item.series.id}`}</span>
            </div>
          )}
          {item.fundraiser && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.FUNDRAISER} />:</strong>
              <span>{item.fundraiser.name || `ID: ${item.fundraiser.id}`}</span>
            </div>
          )}
          {item.team && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.TEAM} />:</strong>
              <span>{item.team.name || `ID: ${item.team.id}`}</span>
            </div>
          )}
          {item.collection && (
            <div className={styles.deleteModalRow}>
              <strong><Translate value={i18nKeys.RESULTS.SUPERTEAM} />:</strong>
              <span>{item.collection.name || `ID: ${item.collection.id}`}</span>
            </div>
          )}
        </div>

        {/* Warning Message */}
        <Message negative className={styles.deleteModalWarning}>
          <Message.Header>
            <Icon name="exclamation triangle" />
            Warning
          </Message.Header>
          <p><Translate value={i18nKeys.DELETE_MODAL.WARNING} /></p>
        </Message>

        {/* Ceremonial Checkbox */}
        <div className={styles.deleteModalCheckbox}>
          <Checkbox
            label={I18n.t(i18nKeys.DELETE_MODAL.CHECKBOX_LABEL)}
            checked={isAccepted}
            onChange={(_, data) => setIsAccepted(!!data.checked)}
            disabled={isDeleting}
          />
        </div>

        {/* Error Message */}
        {error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button
          onClick={handleClose}
          disabled={isDeleting}
        >
          <Translate value={i18nKeys.DELETE_MODAL.CANCEL_BUTTON} />
        </Button>
        <Button
          negative
          onClick={handleConfirm}
          disabled={!isAccepted || isDeleting}
          loading={isDeleting}
        >
          <Icon name="trash" />
          <Translate value={i18nKeys.DELETE_MODAL.CONFIRM_BUTTON} />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DeleteConfirmationModal;
